import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, InputOnChangeData } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { ISearchBoxProps, useSearchBoxValue } from './searchBoxShared';
import SuggestingSearchBox from './SuggestingSearchBox';

/**
 * The storefront search bar.
 *
 * Extracted because the same input existed in five places (the three header variants, the home page
 * hero, and the search page). Adding autocomplete to each independently would have meant five copies
 * of the debounce, keyboard handling and column layout.
 *
 * Deliberately split in two. This component is plain: local state, one navigate, nothing else. All of
 * the suggestion machinery — the RTK Query hooks, the localization lookup, the popout — lives in
 * SuggestingSearchBox, which is mounted only when the storefront has the feature switched on.
 *
 * That split is load-bearing, not tidiness. A storefront that has not adopted autocomplete keeps
 * rendering exactly the markup it rendered before this feature existed, subscribing to nothing new and
 * requiring no new context. The header golden-render test asserts that invariant directly, and it is
 * what makes this change safe to ship dark.
 *
 * Two switches have to agree before a popout appears: the storefront must have suggestions built at
 * all (suggestionsAvailable), and THIS bar must be configured to show them (autocomplete.enabled).
 * That is what lets the header bar have suggestions while the search page bar does not.
 */
const SearchBox: React.FC<ISearchBoxProps> = (props) => {
    const nav = useNavigate();
    const { keywords, setKeywords } = useSearchBoxValue(props);
    const { onSearch } = props;

    const search = (submitted: string) => {
        if (onSearch) onSearch(submitted);
        else nav(`/search?keywords=${encodeURIComponent(submitted)}`);
    };

    if (props.suggestionsAvailable === true && props.autocomplete?.enabled === true)
        return <SuggestingSearchBox {...props} />;

    return <Input
        style={props.style}
        placeholder={props.placeholder}
        size='large'
        value={keywords}
        onChange={(_e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setKeywords(data.value)}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') search(keywords); }}
        contentAfter={<SearchRegular onClick={() => search(keywords)} style={{ cursor: 'pointer' }} />}
    />;
};

export default SearchBox;
